import { FunctionComponent } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { SvpSeason } from '@vgtv/api-client/lib/svp_season';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { useViewportType } from '../../hooks/useViewportType';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { Slider } from '../Slider/Slider';
import { AssetPlaceholder } from '../AssetPlaceholder/AssetPlaceholder';
import { AssetHorizontal } from '../AssetHorizontal/AssetHorizontal';
import { useSliderItemWidth } from '../../hooks/useSliderItemWidth';
import { usePlaceholders } from '../../hooks/usePlaceholders';
import { useSeasonAssets } from '../../hooks/useSeasonAssets';
import { SeasonPicker } from '../SeasonPicker/SeasonPicker';

interface NextUpSliderProps {
  seasons: SvpSeason[];
  onAssetClick?: (asset: Asset, event: MouseEvent) => void;
}

const createSeasonSelectOptions = (seasons: SvpSeason[]) => {
  return seasons.map((season) => {
    return {
      value: season.seasonNumber,
      label: season.seasonNumber,
    };
  });
};

export const SeasonSlider: FunctionComponent<NextUpSliderProps> = ({
  seasons,
  onAssetClick,
}) => {
  const options = useMemo(() => createSeasonSelectOptions(seasons), [seasons]);
  const { asset } = useCurrentAsset();
  const [selectedSeasonNumber, setSelectedSeasonNumber] = useState(
    asset?.series?.seasonNumber || 1
  );
  const { isMobile } = useViewportType();

  const { assets, fetchNextPage } = useSeasonAssets({
    seasonNumber: selectedSeasonNumber,
    categoryId: asset?.category.id,
  });

  const { placeholders } = usePlaceholders(assets.length > 0);
  const { sliderItemWidth } = useSliderItemWidth();

  const categoryTitle = asset?.category?.title || '';

  const header = (
    <SeasonPicker
      value={selectedSeasonNumber}
      options={options}
      onChange={setSelectedSeasonNumber}
      size={isMobile ? 'small' : 'large'}
      categoryTitle={categoryTitle}
    />
  );

  return (
    <Slider
      header={header}
      onIntersection={fetchNextPage}
      intersectionMargin={sliderItemWidth}
    >
      {placeholders.map((_, index) => (
        <AssetPlaceholder key={index} disableDescription={isMobile} />
      ))}
      {assets.map((item) => (
        <AssetHorizontal
          key={item.id}
          asset={item}
          width={sliderItemWidth}
          disableDescription={isMobile}
          onAssetClick={onAssetClick}
          context="season"
        />
      ))}
    </Slider>
  );
};
